/**
 * Resolving a game-data path the way the engine resolves it.
 *
 * The editor needs four things that are *not* in SAVE.DAT and are usually not
 * loose on disk either: `data\vault13.gam` (GVAR names), `proto\items\items.lst`
 * plus the `.pro` files it names (item identity and type), the item text in
 * `text\<lang>\game\pro_item.msg`, and the inventory art under `art\inven\`.
 * On a normal install every one of them is inside master.dat.
 *
 * This was the bug behind "the Globals tab shows bare numbers": the editor only
 * ever looked for a loose `data/vault13.gam`, which exists on a folder install
 * and on nothing else. A DAT-only install — a dropped master.dat plus
 * patch000.dat, which is what the disc importer produces — has no loose `data/`
 * at all, so the common case was the unnamed case.
 *
 * ## Precedence, from the engine
 *
 * Two mechanisms, and the editor needs both because the four paths above are
 * split across them.
 *
 * `gameOpenDatabase` (engine/src/game.cc:1430-1476) opens archives in reverse
 * priority order — `dbOpen` prepends to the chain — and states the result:
 *
 *     master_patches > critter_patches > mods > patchXXX.dat > ce.dat
 *       > f2_res_dat > critter.dat > master.dat
 *
 * `master_patches` is not an archive: it is the loose `data` *directory*
 * (`settings.system.master_patches_path`, default `"data"`). `xfileOpen`
 * (engine/src/xfile.cc:71) walks that chain, joining a directory xbase's path
 * with the requested path — so `proto\items\items.lst` is looked for at
 * `data\proto\items\items.lst` — and **then**, only if nothing in the chain
 * answered, falls back to opening the path relative to the working directory.
 *
 * That fallback is not a detail. It is the only reason a loose
 * `data/vault13.gam` is ever found at all: the engine asks for
 * `data\vault13.gam`, the directory xbase turns that into
 * `data\data\vault13.gam`, which does not exist, the archives answer on a
 * folder-less install and do not on a folder install, and the working-directory
 * fallback is what finds `<install>/data/vault13.gam`. So the resolution order
 * this module implements is:
 *
 *   1. `<HOME>/data/<path>` — the loose directory xbase
 *   2. the mods layer: `mods/mods_order.txt` entries, later lines first —
 *      sfallLoadMods (engine/src/sfall_ext.cc) dbOpens each listed entry in
 *      file order and dbOpen prepends, so the last line is the highest mod.
 *      An entry may be a DAT2 archive or a plain folder (xfile.cc:562).
 *   3. patchNNN.dat, highest index first, then critter.dat, then master.dat
 *   4. `<HOME>/<path>` — the working-directory fallback
 *
 * The mods layer exists since the Restoration Project install; a recipe mod
 * from the Mod Maker is a folder entry in the same list. The high-res-patch
 * slot is still deliberately absent: the shell never installs f2_res.dat.
 *
 * ## Case
 *
 * DAT2 member names are matched lowercased — `openDatArchive` normalises the
 * whole tree. The loose roots are a real filesystem, so each path is tried in a
 * few shapes, which covers what the importer and the engine actually write.
 *
 * ## Lifetime
 *
 * Opening the archives means reading and indexing master.dat's 23,000-entry
 * tree, so the result is memoised against the `EmFS` it was read from. A
 * restart replaces the FS object, which invalidates the memo for free.
 */

import { activeHome } from '../config';
import type { EmFS } from '../engine';
import { openAnyArchive, type DatArchive } from './archive';

/** Where a member came from. Shown to the player as provenance. */
export interface GameFileHit {
  bytes: Uint8Array;
  /** `data/`, `patch000.dat`, `master.dat`, or `<install root>`. */
  source: string;
}

export interface GameFiles {
  /** Bytes of a game-data path (e.g. `proto/items/items.lst`), or null. */
  read(path: string): Promise<Uint8Array | null>;
  /** Same, plus which source answered. */
  readFrom(path: string): Promise<GameFileHit | null>;
  /** Same as `read`, decoded as latin1 — the encoding these text files use. */
  readText(path: string): Promise<string | null>;
  /**
   * Every known path under `prefix`, from the archives and mod entries (folder
   * mods included). The loose `data/` root is not enumerated (the engine does
   * not enumerate it either); callers list and then `read`, which still lets a
   * loose file win.
   */
  list(prefix: string): string[];
  /** Every source consulted, in precedence order. */
  readonly sources: string[];
  /** True when at least one archive or loose root answered for anything. */
  readonly ok: boolean;
}

/** How many `patchNNN.dat` to probe. The engine stops at the first gap; so do we. */
const MAX_PATCH_INDEX = 32;

const decodeLatin1 = (bytes: Uint8Array): string => {
  let s = '';
  // Chunked: `String.fromCharCode(...bytes)` blows the argument limit on a
  // half-megabyte .msg file.
  for (let i = 0; i < bytes.length; i += 8192) {
    s += String.fromCharCode(...bytes.subarray(i, Math.min(i + 8192, bytes.length)));
  }
  return s;
};

const clean = (path: string): string => path.replace(/\\/g, '/').replace(/^\/+/, '');

/** The shapes of one path worth trying against a real filesystem. */
function caseShapes(path: string): string[] {
  const base = clean(path);
  const shapes = [base, base.toLowerCase(), base.toUpperCase()];
  // Upper-cased leaf, lower-cased directories: what a hand-made patch folder
  // tends to look like.
  const cut = base.lastIndexOf('/');
  if (cut !== -1) shapes.push(base.slice(0, cut).toLowerCase() + '/' + base.slice(cut + 1).toUpperCase());
  return [...new Set(shapes)];
}

function readLoose(FS: EmFS, root: string, path: string): Uint8Array | null {
  for (const shape of caseShapes(path)) {
    try {
      const data = FS.readFile(`${root}/${shape}`, { encoding: 'binary' });
      if (data instanceof Uint8Array) return data;
    } catch {
      /* not this shape */
    }
  }
  return null;
}

function dirExists(FS: EmFS, path: string): boolean {
  try {
    return FS.readdir(path).length > 0;
  } catch {
    return false;
  }
}

/** One mods_order.txt entry the resolver can ask: a DAT2 or a loose folder. */
interface ModSource {
  /** `mods/<entry>`, as shown in provenance. */
  name: string;
  read(path: string): Promise<Uint8Array | null>;
  list(prefix: string): string[];
}

/** mods_order.txt parsed the way sfallLoadMods parses it (sfall_ext.cc). */
export function parseModsOrder(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !/[;#]/.test(l));
}

/** Recursively lists a MEMFS directory as game-root-relative lowercase paths. */
function walkDir(FS: EmFS, root: string, rel: string, out: string[]): void {
  let names: string[];
  try {
    names = FS.readdir(`${root}/${rel}`.replace(/\/$/, ''));
  } catch {
    return;
  }
  for (const name of names) {
    if (name === '.' || name === '..') continue;
    const childRel = rel ? `${rel}/${name}` : name;
    try {
      FS.readdir(`${root}/${childRel}`);
      walkDir(FS, root, childRel, out);
    } catch {
      out.push(childRel.toLowerCase());
    }
  }
}

/**
 * Opens the mods layer, highest priority first (reverse of mods_order.txt line
 * order — dbOpen prepends). Entries that are neither a readable DAT2 nor a
 * directory are skipped, same as the engine skips them.
 */
async function openMods(FS: EmFS): Promise<ModSource[]> {
  const HOME = activeHome();
  const orderBytes = readLoose(FS, `${HOME}/mods`, 'mods_order.txt');
  if (!orderBytes) return [];
  const entries = parseModsOrder(decodeLatin1(orderBytes));

  const sources: ModSource[] = [];
  for (const entry of entries) {
    const root = `${HOME}/mods/${entry}`;
    if (dirExists(FS, root)) {
      sources.push({
        name: `mods/${entry}`,
        read: async (path) => readLoose(FS, root, path),
        list: (prefix) => {
          const all: string[] = [];
          walkDir(FS, root, '', all);
          return all.filter((p) => p.startsWith(prefix));
        },
      });
      continue;
    }
    const dat = await openAnyArchive(FS, root);
    if (dat) {
      sources.push({ name: `mods/${entry}`, read: (path) => dat.read(path), list: (prefix) => dat.list(prefix) });
    }
  }
  return sources.reverse();
}

async function openArchives(FS: EmFS): Promise<DatArchive[]> {
  const HOME = activeHome();
  // Highest priority first, which is the reverse of the order game.cc opens
  // them in.
  const paths: string[] = [];
  for (let i = MAX_PATCH_INDEX - 1; i >= 0; i--) {
    paths.push(`${HOME}/patch${String(i).padStart(3, '0')}.dat`);
  }
  paths.push(`${HOME}/critter.dat`, `${HOME}/master.dat`);

  // Both games name their archives the same way in the same `[system]` keys
  // (`master_dat`/`critter_dat`, config.ts:355 and :422; FO1's
  // `game_init_databases`, fallout1-ce game.cc:1245-1275, reads exactly those),
  // so the path list is shared and only the *container* differs. FO1 ships no
  // patch000.dat, but the probe above costs a failed stat and honours one if a
  // player has added it.
  const opened: DatArchive[] = [];
  for (const path of paths) {
    const dat = await openAnyArchive(FS, path);
    if (dat) opened.push(dat);
  }
  return opened;
}

async function build(FS: EmFS): Promise<GameFiles> {
  const HOME = activeHome();
  const [mods, archives] = [await openMods(FS), await openArchives(FS)];
  const looseDir = dirExists(FS, `${HOME}/data`);

  const sources: string[] = [];
  if (looseDir) sources.push('data/');
  for (const m of mods) sources.push(m.name);
  for (const a of archives) sources.push(a.path.slice(HOME.length + 1));
  sources.push('install root');

  const cache = new Map<string, Promise<GameFileHit | null>>();

  const resolve = async (path: string): Promise<GameFileHit | null> => {
    // 1. the loose `data` directory xbase.
    if (looseDir) {
      const hit = readLoose(FS, `${HOME}/data`, path);
      if (hit) return { bytes: hit, source: 'data/' };
    }
    // 2. the mods layer, highest priority (last mods_order.txt line) first.
    for (const mod of mods) {
      const bytes = await mod.read(path);
      if (bytes) return { bytes, source: mod.name };
    }
    // 3. the archives, highest priority first.
    for (const archive of archives) {
      const bytes = await archive.read(path);
      if (bytes) return { bytes, source: archive.path.slice(HOME.length + 1) };
    }
    // 4. the working-directory fallback. This is what finds a folder install's
    //    own `data/vault13.gam`.
    const cwd = readLoose(FS, HOME, path);
    return cwd ? { bytes: cwd, source: 'install root' } : null;
  };

  const readFrom = (path: string): Promise<GameFileHit | null> => {
    const key = clean(path).toLowerCase();
    let pending = cache.get(key);
    if (!pending) {
      pending = resolve(path).catch(() => null);
      cache.set(key, pending);
    }
    return pending;
  };

  return {
    readFrom,
    async read(path) {
      return (await readFrom(path))?.bytes ?? null;
    },
    async readText(path) {
      const bytes = (await readFrom(path))?.bytes;
      return bytes ? decodeLatin1(bytes) : null;
    },
    list(prefix) {
      const want = clean(prefix).toLowerCase();
      const seen = new Set<string>();
      for (const mod of mods) {
        for (const member of mod.list(want)) seen.add(member);
      }
      for (const archive of archives) {
        for (const member of archive.list(want)) seen.add(member);
      }
      return [...seen].sort();
    },
    sources,
    ok: looseDir || archives.length > 0,
  };
}

const EMPTY: GameFiles = {
  read: async () => null,
  readFrom: async () => null,
  readText: async () => null,
  list: () => [],
  sources: [],
  ok: false,
};

let cached: { fs: EmFS; files: Promise<GameFiles> } | null = null;

/**
 * The mounted install's files, memoised per filesystem. Never rejects: an
 * install with nothing readable yields a `GameFiles` whose `ok` is false and
 * whose every read answers null, and every caller has a fallback for that.
 */
export function gameFiles(FS: EmFS): Promise<GameFiles> {
  if (cached && cached.fs === FS) return cached.files;
  const files = build(FS).catch(() => EMPTY);
  cached = { fs: FS, files };
  return files;
}

/** Drops the memo. Only the tests need this. */
export function resetGameFilesCache(): void {
  cached = null;
}
